import type { Quote, QuoteLineItem } from '@/gen/cortado/v1/invoices_pb'
import { formatCurrency } from '@/lib/currency-utils'

/** One side of a compared line: the quote's lines that share a key, summed. */
export interface QuoteCompareSide {
  quantity: number
  unit: string
  totalCents: number
}

/** A line item as it appears on either quote; a side is undefined when that quote lacks it. */
export interface QuoteCompareLine {
  key: string
  name: string
  left?: QuoteCompareSide
  right?: QuoteCompareSide
}

/** A totals row; zero-on-both rows (no discount, no tip) are left out. */
export interface QuoteCompareTotal {
  key: 'subtotal' | 'discount' | 'tax' | 'total' | 'tip' | 'totalWithTip'
  label: string
  leftCents: number
  rightCents: number
}

/**
 * Lines match on their catalog item, so a renamed catalog line still lines up;
 * custom lines match on their name. A quote with the same item on several
 * events shows it once, quantities and totals summed.
 */
function lineKey(line: QuoteLineItem): string {
  return line.sourceItemId ? `item:${line.sourceItemId}` : `name:${line.name.trim().toLowerCase()}`
}

function sideOf(lines: QuoteLineItem[]): Map<string, { name: string; side: QuoteCompareSide }> {
  const byKey = new Map<string, { name: string; side: QuoteCompareSide }>()
  for (const line of [...lines].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const key = lineKey(line)
    const entry = byKey.get(key)
    if (entry) {
      entry.side.quantity += line.quantity
      entry.side.totalCents += Number(line.lineTotalCents)
    } else {
      byKey.set(key, {
        name: line.name,
        side: { quantity: line.quantity, unit: line.unit, totalCents: Number(line.lineTotalCents) },
      })
    }
  }
  return byKey
}

/** The two quotes' line items side by side: the left quote's order, then lines only the right has. */
export function quoteCompareLines(left: Quote, right: Quote): QuoteCompareLine[] {
  const leftLines = sideOf(left.lineItems)
  const rightLines = sideOf(right.lineItems)
  const rows: QuoteCompareLine[] = []
  for (const [key, { name, side }] of leftLines) {
    rows.push({ key, name, left: side, right: rightLines.get(key)?.side })
  }
  for (const [key, { name, side }] of rightLines) {
    if (!leftLines.has(key)) rows.push({ key, name, right: side })
  }
  return rows
}

export function quoteCompareTotals(left: Quote, right: Quote): QuoteCompareTotal[] {
  const tip = (quote: Quote) => Number(quote.totalWithTipCents - quote.totalCents)
  const rows: QuoteCompareTotal[] = [
    { key: 'subtotal', label: 'Subtotal', leftCents: Number(left.subtotalCents), rightCents: Number(right.subtotalCents) },
    // Shown as a negative amount, the way it comes off the subtotal.
    { key: 'discount', label: 'Discount', leftCents: Number(-left.discountCents), rightCents: Number(-right.discountCents) },
    { key: 'tax', label: 'Sales tax', leftCents: Number(left.salesTaxCents), rightCents: Number(right.salesTaxCents) },
    { key: 'total', label: 'Total', leftCents: Number(left.totalCents), rightCents: Number(right.totalCents) },
    { key: 'tip', label: 'Tip', leftCents: tip(left), rightCents: tip(right) },
    {
      key: 'totalWithTip',
      label: 'Total with tip',
      leftCents: Number(left.totalWithTipCents),
      rightCents: Number(right.totalWithTipCents),
    },
  ]
  const hasTip = tip(left) > 0 || tip(right) > 0
  return rows.filter((row) => {
    if (row.key === 'discount') return row.leftCents !== 0 || row.rightCents !== 0
    if (row.key === 'tip' || row.key === 'totalWithTip') return hasTip
    return true
  })
}

/** "$1,250.00", or "−$50.00" for a negative amount. */
export function quoteCompareAmount(cents: number): string {
  return `${cents < 0 ? '−' : ''}$${formatCurrency(Math.abs(cents))}`
}

/** The right quote against the left: "+$200.00", "−$50.00", or "Same". */
export function quoteCompareDifference(leftCents: number, rightCents: number): string {
  const diff = rightCents - leftCents
  if (diff === 0) return 'Same'
  return `${diff > 0 ? '+' : '−'}$${formatCurrency(Math.abs(diff))}`
}

/** "3 × Per Guest"; a whole quantity drops its decimals. */
export function quoteCompareQuantity(side: QuoteCompareSide): string {
  const quantity = Number.isInteger(side.quantity) ? String(side.quantity) : String(Number(side.quantity.toFixed(2)))
  return side.unit ? `${quantity} × ${side.unit}` : quantity
}
